import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import Form from "react-bootstrap/Form";
import { Button, Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faTrash, faUnlock } from "@fortawesome/free-solid-svg-icons";
import { useForm, FormProvider } from "react-hook-form";
import { usePermissionModal } from "contexts/PermissionModalContext";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import DeleteRestoreModal from "../../../components/DeleteRestoreModal";
import DeletedItemBanner from "components/common/DeletedItemBanner";
import DeletedBorderCard from "components/common/DeletedBorderCard";
import PermissoesManager from "./PermissoesManager";
import LogoutGroupUsersModal from "components/common/LogoutGroupUsersModal";
import { useInvalidateMenuCache } from "hooks/useMenu";
import { useToast } from "providers/ToastProvider";

// Define breadcrumbs for the group edit page
const grupoBreadcrumbItems = () => [
  {
    label: "Cadastros",
    url: "#!",
  },
  {
    label: "Grupos de Acesso",
    url: "/cadastros/grupos",
  },
  {
    label: "Editar",
    active: true,
  },
];

// Define the types for the form and API responses
interface GrupoFormValues {
  id: number;
  codigo: number;
  descricao: string;
  permissoes: number[];
  isDeleted?: boolean;
  deletedAt?: string | null;
  LogoutUsers?: boolean;
}


const EditarGrupo = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidateMenuCache = useInvalidateMenuCache();
  const { showToast } = useToast();
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [modalOperation, setModalOperation] = useState<
    null | "delete" | "restore"
  >(null);

  // Use the permission modal hook
  const { getMutationErrorHandler } = usePermissionModal();

  // Add logout users modal state
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<GrupoFormValues | null>(null);

  const formMethods = useForm<GrupoFormValues>({
    defaultValues: {
      id: 0,
      codigo: 0,
      descricao: "",
      permissoes: [],
    },
  });

  const isDirtyRef = useRef(false);

  // Effect to update the dirty ref whenever form state changes
  useEffect(() => {
    isDirtyRef.current = formMethods.formState.isDirty;
  }, [formMethods.formState.isDirty]);

  // Fetch grupo details
  const {
    isLoading: isLoadingGrupo,
    data: grupoData,
    refetch,
  } = useQuery({
    queryKey: ["grupo", id],
    queryFn: async () => {
      const response = await axios.get(`/api/grupo/${id}`);
      if (!response.data.isSuccess) {
        throw new Error(response.data.message || "Erro ao carregar grupo");
      }
      return response.data.data; // Acesso ao campo 'data' dentro do Result
    },
    enabled: !!id,
  });

  // Effect to set form data once grupo data is loaded
  useEffect(() => {
    if (grupoData) {
      formMethods.reset({
        id: grupoData.id,
        codigo: grupoData.codigo,
        descricao: grupoData.descricao,
        permissoes: grupoData.permissoes || [],
        isDeleted: grupoData.isDeleted,
        deletedAt: grupoData.deletedAt,
      });
    }
  }, [grupoData, formMethods]);

  const updateGrupoMutation = useMutation({
    mutationFn: async (formData: GrupoFormValues) => {
      const response = await axios.put(`/api/grupo/${id}`, formData);
      if (!response.data.isSuccess) {
        throw new Error(response.data.message || "Erro ao atualizar grupo");
      }
      return response.data.data; // Acesso ao campo 'data' dentro do Result
    },
    onSuccess: (response) => {
      // Reset dirty status
      formMethods.reset(formMethods.getValues(), {
        keepValues: true,
        keepDirtyValues: false,
      });
      isDirtyRef.current = false;

      // Invalidar o cache de menu para refletir possíveis mudanças nas permissões
      invalidateMenuCache();

      showToast(<>Grupo <b>{formMethods.getValues("codigo")}</b> foi atualizado com sucesso!</>, "success");

      navigate("/cadastros/grupos");
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para editar grupos de acesso.",
      (error) => {
        showToast(error.response?.data?.message || error.message || "Erro ao atualizar grupo", "error");
      }
    ),
  });

  const deleteGrupoMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.delete(`/api/grupo/${id}`);
      if (!response.data.isSuccess) {
        throw new Error(response.data.message || "Erro ao executar operação");
      }
      return response.data;
    },
    onError: getMutationErrorHandler(
      modalOperation === "delete"
        ? "Você não possui permissão para excluir grupos de acesso."
        : "Você não possui permissão para restaurar grupos de acesso.",
      (error) => {
        // Handle non-permission errors
        showToast(
          error.response?.data?.message || error.message ||
          (modalOperation === "delete"
            ? "Erro ao excluir grupo"
            : "Erro ao restaurar grupo"),
          "error"
        );
        setModalOperation(null);
      }
    ),
    onSuccess: (response) => {
      // Invalidar o cache de menu para refletir possíveis mudanças nas permissões
      invalidateMenuCache();

      if (modalOperation === "restore") {
        // Group was restored
        showToast(
          <>Grupo <b>{formMethods.getValues("codigo")}</b> restaurado com sucesso!</>,
          "success"
        );
        refetch();
        setModalOperation(null);
      } else {
        // Group was deleted
        navigate("/cadastros/grupos", {
          state: {
            deleteSuccess: true,
            deletedName: formMethods.getValues("descricao"),
            deletedCode: formMethods.getValues("codigo"),
            timestamp: new Date().getTime(),
          },
        });
      }
    },
  });

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!isDirtyRef.current) return false;
    if (formMethods.formState.isSubmitting) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;

    setPendingNavigation(nextLocation.pathname);
    setShowNavigationModal(true);

    return true;
  });

  const handleDeleteOrRestore = (isDelete: boolean) => {
    setModalOperation(isDelete ? "delete" : "restore");
  };

  const handleConfirmOperation = async () => {
    if (modalOperation === "delete" && grupoData?.isDeleted) {
      // If we're trying to delete an already deleted group, change to restore
      setModalOperation("restore");
    }
    // Start the mutation after ensuring the operation type is correct
    await deleteGrupoMutation.mutate();
  };

  const handleCancelOperation = () => {
    setModalOperation(null);
  };

  // Function to handle navigation confirmation
  const handleNavigationConfirm = () => {
    setShowNavigationModal(false);

    if (pendingNavigation) {
      isDirtyRef.current = false;
      blocker.proceed?.();
      navigate(pendingNavigation);
    }
  };

  // Function to cancel navigation
  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    blocker.reset?.();
  };

  const handleSubmit = async (formData: GrupoFormValues) => {
    try {
      // Se estiver editando outro grupo, mostrar a modal de confirmação
      setPendingFormData(formData);
      setShowLogoutModal(true);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // Função para lidar com o deslogamento imediato dos usuários
  const handleLogoutUsers = () => {
    if (pendingFormData) {
      // Enviar solicitação com flag para deslogar usuários
      const dataWithLogout = { ...pendingFormData, LogoutUsers: true };
      updateGrupoMutation.mutate(dataWithLogout);
      setShowLogoutModal(false);
    }
  };

  // Função para continuar sem deslogar os usuários
  const handleContinueWithoutLogout = () => {
    if (pendingFormData) {
      // Enviar solicitação sem flag para deslogar usuários
      const dataWithoutLogout = { ...pendingFormData, LogoutUsers: false };
      updateGrupoMutation.mutate(dataWithoutLogout);
      setShowLogoutModal(false);
    }
  };

  const isLoading = isLoadingGrupo;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <UnsavedChangesModal
        show={showNavigationModal}
        onConfirm={handleNavigationConfirm}
        onCancel={handleNavigationCancel}
      />

      <DeleteRestoreModal
        show={modalOperation !== null}
        isDeleteOperation={modalOperation === "delete"}
        onConfirm={handleConfirmOperation}
        onCancel={handleCancelOperation}
        isLoading={deleteGrupoMutation.isPending}
        itemName={formMethods.getValues("descricao")}
        itemCode={formMethods.getValues("codigo")}
        titleMessage={
          modalOperation === "delete" ? "Confirmar Exclusão" : "Restaurar Grupo"
        }
        confirmMessage={
          modalOperation === "delete"
            ? "Você está prestes a excluir o grupo:"
            : "Você está prestes a restaurar o acesso do grupo:"
        }
        infoMessage={
          modalOperation === "delete"
            ? "Ao excluir este grupo, ele será marcado como inativo e não poderá ser usado em outras partes do sistema. Esta ação poderá ser revertida posteriormente pelo administrador."
            : "Ao restaurar este grupo, ele voltará a estar disponível no sistema. Todas as permissões associadas serão preservadas."
        }
        actionButtonText={
          modalOperation === "delete" ? "Excluir Grupo" : "Restaurar Grupo"
        }
      />

      {/* Add LogoutGroupUsersModal */}
      <LogoutGroupUsersModal
        show={showLogoutModal}
        onLogoutNow={handleLogoutUsers}
        onLogoutLater={handleContinueWithoutLogout}
        groupName={formMethods.getValues("descricao") || ""}
      />

      <PageBreadcrumb items={grupoBreadcrumbItems()} />

      <FormProvider {...formMethods}>
        <Form
          noValidate
          className="mb-9"
          onSubmit={formMethods.handleSubmit(handleSubmit)}
        >
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div className="mb-3 mb-md-0">
              <h2 className="mb-2">Editar um Grupo de Acesso</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações do grupo
              </h5>
            </div>
            {!grupoData?.isDeleted && (
              <div>
                <>
                  <Button
                    variant="outline-danger"
                    className="me-2"
                    onClick={() => handleDeleteOrRestore(true)}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Excluir
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={updateGrupoMutation.isPending}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {updateGrupoMutation.isPending
                      ? "Salvando..."
                      : "Salvar Alterações"}
                  </Button>
                </>
              </div>
            )}
          </div>

          {grupoData?.isDeleted && (
            <DeletedItemBanner
              title="Este grupo foi excluído e não está mais ativo"
              message="Todas as informações são apresentadas apenas para referência. Para editar o grupo, restaure-o primeiro."
              deletedAt={grupoData?.deletedAt}
              onRestore={() => handleDeleteOrRestore(false)}
              restoreButtonText="Restaurar Grupo"
              badgeText="Grupo Inativo"
            />
          )}

          {/* Informações do Grupo */}
          <DeletedBorderCard isDeleted={grupoData?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">Informações do Grupo</h5>
              <div className="row g-3">
                <div className="col-12 col-md-3 mb-3">
                  <Form.Group controlId="codigo">
                    <Form.Label>Código</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Código"
                      {...formMethods.register("codigo")}
                      disabled
                    />
                  </Form.Group>
                </div>
                <div className="col-12 col-md-9 mb-3">
                  <Form.Group controlId="descricao">
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Digite o nome do grupo"
                      {...formMethods.register("descricao", {
                        required: "Nome do grupo é obrigatório",
                      })}
                      isInvalid={!!formMethods.formState.errors.descricao}
                      disabled={grupoData?.isDeleted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formMethods.formState.errors.descricao?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>
            </Card.Body>
          </DeletedBorderCard>

          {/* Permissões do Grupo */}

          <DeletedBorderCard isDeleted={grupoData?.isDeleted}>
            <PermissoesManager disabled={grupoData?.isDeleted} />
          </DeletedBorderCard>

          <div className="d-flex flex-column flex-md-row justify-content-between mt-4 gap-3 gap-md-0">
            <Button
              variant="secondary"
              type="button"
              className="order-2 order-md-1"
              onClick={() => navigate("/cadastros/grupos")}
            >
              Cancelar
            </Button>

            {!grupoData?.isDeleted ? (
              <div className="order-1 order-md-2 d-flex gap-2">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={() => handleDeleteOrRestore(true)}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Excluir Grupo
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={updateGrupoMutation.isPending}
                >
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {updateGrupoMutation.isPending
                    ? "Salvando..."
                    : "Salvar Alterações"}
                </Button>
              </div>
            ) : (
              <Button
                variant="danger"
                onClick={() => handleDeleteOrRestore(false)}
                className="order-1 order-md-2 rounded-pill px-4"
              >
                <FontAwesomeIcon icon={faUnlock} className="me-2" />
                Restaurar Grupo
              </Button>
            )}
          </div>
        </Form>
      </FormProvider>
    </div>
  );
};

export default EditarGrupo;
